/**
 * Ramps Store - Centralized state management for the cash-out flow
 * Using TanStack Store for simple and effective state management
 */

import { Store } from "@tanstack/store";
import { getCountriesWithSubdivisions } from "@/constants/subdivisions";
import {
	PASSPORT_ID_TYPE,
	requiresPassportSwitch,
} from "@/utils/idRequirements";
import type {
	BankAccount,
	CashInLocation,
	Country,
	CreateProfileResponse,
	PayoutEntityChoice,
	PhonePreCheckResponse,
	PickupLocation,
	ProfileResponse,
	QuoteResponse,
	SendCommitRequest,
	SendCommitResponse,
	SenderProfile,
	ServiceOptionCode,
	ValidationResponse,
} from "../api/types/moneygram";
import type { OnfidoCompleteData } from "../components/ramps/KycVerification";
import { normalizeOperation } from "../lib/normalize-operation";

/**
 * Enforce the foreign-ID → passport rule. A stored non-passport ID is only
 * valid when issued in the selected payout country; if a country change or a
 * profile prefill leaves a mismatched non-passport ID in place, reset the ID so
 * the user is routed back through the identification step to provide a passport.
 * Clearing the ID number/issue country also flips isDataFromTrustedSource off,
 * which un-skips the identification page in the flow logic.
 */
function enforcePassportIdRule<
	T extends {
		idType: string;
		idNumber: string;
		idIssueCountry: string;
		idIssueState: string;
		payoutCountry: string;
	},
>(state: T): T {
	if (
		requiresPassportSwitch({
			idType: state.idType,
			idIssueCountry: state.idIssueCountry,
			payoutCountry: state.payoutCountry,
		})
	) {
		return {
			...state,
			idType: PASSPORT_ID_TYPE,
			idNumber: "",
			idIssueCountry: "",
			idIssueState: "",
		};
	}
	return state;
}

export interface RampsState {
	// Flow control
	currentStep:
		| "calculator"
		| "phone"
		| "name"
		| "address"
		| "date-of-birth"
		| "identification"
		| "digital-offramp-info"
		| "additional-info"
		| "review"
		| "status";

	// Reference data
	countries: Country[];
	suggestedCountry?: string; // Country suggested by service options

	// Calculator data

	residencyCountry: string; // Where user lives (for address)
	payoutCountry: string; // Where user wants to receive money

	locks: Set<string>; // Locked fields that shouldn't be auto-updated
	state: string;
	amount: string;
	currency: string;
	minAmount?: number; // Minimum amount from feature flag
	maxAmount?: number; // Maximum amount from feature flag
	maxSendLimit?: number; // Maximum send limit in USDC from feature flag
	kycPollingTime?: number; // KYC polling timer duration from feature flag
	kycFlowEnabled?: boolean; // Whether KYC flow is enabled from feature flag
	cashInFlowEnabled?: boolean; // Whether cash in (deposit) flow is enabled from feature flag

	// Phone verification
	phone: string;
	phoneCountryCode: string; // Dial code for API (e.g., "+1")
	phoneCountrySelection: string; // Full value for UI (e.g., "1-USA")

	// Personal information
	firstName: string;
	middleName: string;
	lastName: string;
	secondLastName: string;
	isLegalNameConfirmed: boolean;
	dateOfBirth: string;
	birthCountryCode: string;
	birthCity: string;
	notificationLanguagePreference: string;
	politicalExposedPerson: boolean;
	email: string;
	genderCode: string | null;
	citizenshipCountryCode: string | null;

	// Address information
	address: string; // Maps to address line 1
	address2: string; // Maps to address line 2 (optional - apartment, suite, etc.)
	address3: string; // Maps to address line 3 (optional)
	city: string;
	zipCode: string;
	addressCountry: string;
	addressState: string;
	isManualAddress: boolean;

	// Identification
	idType: string;
	idNumber: string;
	idIssueCountry: string;
	idIssueState: string;

	// Calculated values
	exchangeRate: string;
	fees: string;
	total: string;
	payoutCurrency: string;

	// Transaction data
	// TODO: After legacy repo migration is complete, consider separating JWT and OAuth tokens
	// into distinct fields (jwtToken/oauthToken) for better clarity and debugging.
	// Current approach is intentionally kept simple to avoid merge conflicts during migration.
	token: string;
	transactionId: string;
	walletAddress: string;
	apiVersion: "v2" | "v3" | "v5"; // Detected once at initialization

	// V3 prepare endpoint response data (for JWT/v3 flow)
	// These values come from /v3/transactions/prepare instead of URL params
	operation: string;
	lang: string;
	/**
	 * Source of `lang`. When `"url-app-languages"` or `"url-lang"` we treat the
	 * partner-supplied URL value as authoritative and refuse to overwrite it
	 * from `/prepare` or `setContextData` — those would otherwise clobber the
	 * URL-driven choice with a stale value persisted on the transaction.
	 */
	langSource:
		| "url-app-languages"
		| "url-lang"
		| "browser"
		| "prepare"
		| "default";
	assetCode: string;
	walletProvider: string;
	prepareAmount?: string; // Amount from v3 prepare endpoint (locks input when present)
	isPrepareComplete: boolean; // True when prepare (v3) or setContextData (v2/v5) has completed
	isPrepareFailed: boolean; // True when the v3 prepare call failed after retries — landing pages show the error splash

	// Quote data
	quoteResponse?: QuoteResponse;

	// Validation payload
	validatePayload?: SendCommitRequest;

	// Transaction response data
	transactionResponse?: ValidationResponse;

	// Send commit response data
	commitResponse?: {
		transactionId: string;
		transactionCryptoNetworkInstructions: {
			accountAddress: string;
			operationMemo: string;
		};
		sep24TransactionResponse?: SendCommitResponse["sep24TransactionResponse"];
		referenceNumber?: string; // Optional - withdraw flow uses this
		confirmationCode?: string; // Optional - deposit flow uses this
		status?: string; // Optional top-level status for future v5 enhancement
	};

	// Selected pickup location
	selectedLocation?: PickupLocation | null;

	// Cash-in location
	cashInLocation?: CashInLocation | null;

	// Cash-in send (pay-in) currency: serviceOptions DIRECT_TO_ACCT sendCurrencyCodes[0].
	// Set by the deposit page so the review page can rebuild FeeQuote on location change.
	cashInSendCurrency?: string;

	// User coordinates from serviceOptions API (fallback when Geolocation API is denied)
	userLatitude: string;
	userLongitude: string;

	// Digital offramps
	serviceOption: ServiceOptionCode;
	serviceOptionRoutingCode: string;
	selectedBankChoice?: PayoutEntityChoice;

	// GFFP Bank Fields for bank services (BANK_DEPOSIT, DIRECT_TO_ACCT)
	bankFieldsGFFP?: {
		schema: Array<{
			field: string;
			dataType: string;
			required: boolean;
			fieldLabel?: string;
			displayOrder?: string;
			fieldMin?: string;
			fieldMax?: string;
			enumerationItems?: Array<{ value: string; label: string }>;
		}>;
		values: Record<string, unknown>;
	};

	// Flag to indicate if bank account is valid
	isValidBankAccount: boolean;

	// Digital profile data (phone is unique identifier, multiple wallets can link to same profile)
	digitalProfileId?: string;
	selectedBankAccount?: BankAccount;

	// Profile creation optional fields
	enrolInRewards: string | null;
	notificationPreferences: string | null;

	// Profile lookup and creation tracking
	profileCreationCompleted: boolean; // Track if we've created profile for new user
	walletLookupCompleted: boolean; // Track if wallet lookup was performed
	phoneLookupCompleted: boolean; // Track if phone lookup was performed
	walletAssociationCompleted: boolean; // Track if wallet was associated to new profile

	// additionalInfoGFFP?: Record<string, string | Record<string, unknown>>;

	// KYC verification fields (provider-agnostic naming)
	kycToken: string;
	kycApplicantId: string;
	isStepUpRequired: boolean;
	isAllowedToProceed: boolean;
	kycVerificationCompleted: boolean;
	kycVerificationData?: OnfidoCompleteData; // Data returned from KYC SDK (document/face IDs)
	kycDataSource: "api" | "manual"; // Track whether KYC data came from API or manual input

	// KYC check fields (post-SDK verification)
	kycDocumentIds: string[]; // Extracted document IDs from SDK
	kycCheckStatus:
		| "idle"
		| "checking"
		| "complete"
		| "declined"
		| "pending"
		| "timeout";

	/**
	 * Transaction IDs for which the user has already submitted a successful
	 * `PUT /refund/transactions/{id}/commit` in this session. Used to gate the
	 * "Cancel transfer" button on the status page during the backend's
	 * eventually-consistent propagation window (where `isRefundAllowedFlag`
	 * may still return true for minutes after a successful commit) so the
	 * user cannot submit duplicate cancellations.
	 */
	cancellationSubmittedTxIds: string[];
}

const initialState: RampsState = {
	currentStep: "calculator",
	countries: [],
	residencyCountry: "",
	payoutCountry: "",
	locks: new Set(),
	state: "",
	amount: "",
	currency: "USDC",
	phone: "",
	phoneCountryCode: "",
	phoneCountrySelection: "",
	firstName: "",
	middleName: "",
	lastName: "",
	secondLastName: "",
	isLegalNameConfirmed: false,
	dateOfBirth: "",
	birthCountryCode: "",
	birthCity: "",
	notificationLanguagePreference: "en-US",
	politicalExposedPerson: false,
	email: "",
	genderCode: null,
	citizenshipCountryCode: null,
	address: "",
	address2: "",
	address3: "",
	city: "",
	zipCode: "",
	addressCountry: "",
	addressState: "",
	isManualAddress: false,
	idType: "state-id",
	idNumber: "",
	idIssueCountry: "",
	idIssueState: "",
	exchangeRate: "1.00",
	fees: "",
	total: "",
	payoutCurrency: "USD",
	token: "",
	transactionId: "",
	walletAddress: "",
	apiVersion: "v3",
	operation: "withdraw",
	lang: "en-US",
	langSource: "default",
	assetCode: "USDC",
	walletProvider: "",
	isPrepareComplete: false,
	isPrepareFailed: false,
	transactionResponse: undefined,
	selectedLocation: null,
	bankFieldsGFFP: undefined,
	// additionalInfoGFFP: undefined,
	validatePayload: undefined,
	serviceOption: "WILL_CALL",
	serviceOptionRoutingCode: "",
	isValidBankAccount: false,
	digitalProfileId: undefined,
	selectedBankAccount: undefined,
	enrolInRewards: null,
	notificationPreferences: null,
	profileCreationCompleted: false,
	walletLookupCompleted: false,
	phoneLookupCompleted: false,
	walletAssociationCompleted: false,
	kycToken: "",
	kycApplicantId: "",
	isStepUpRequired: false,
	isAllowedToProceed: false,
	kycVerificationCompleted: false,
	kycDataSource: "manual", // Default to manual for backward compatibility
	kycDocumentIds: [],
	kycCheckStatus: "idle",
	userLatitude: "",
	userLongitude: "",
	cancellationSubmittedTxIds: [],
};

export const rampsStore = new Store(initialState);

/**
 * Selector: has the user already submitted a successful refund commit for
 * this transaction in the current session? Reads `rampsStore.state` directly
 * so callers can use it outside React (e.g., from event handlers). For
 * reactive usage inside components, wrap with `useRampsStore((s) =>
 * s.cancellationSubmittedTxIds.includes(txId))`.
 */
export function hasCancellationBeenSubmitted(txId?: string): boolean {
	if (!txId) return false;
	return rampsStore.state.cancellationSubmittedTxIds.includes(txId);
}

// Helper function to calculate fees and totals
const calculateTotals = (amount: string) => {
	const amountNum = Number.parseFloat(amount) || 0;
	const fees = 0; // Fixed fee for demo
	const total = amountNum;

	return {
		fees: fees.toFixed(2),
		total: total.toFixed(2),
		exchangeRate: "1.00", // 1:1 rate for USDC to USD
	};
};

/**
 * Extract KYC data from API compliance payload
 * Works with ProfileResponse, PhonePreCheckResponse, or CreateProfileResponse
 */
export function extractKycDataFromCompliance(
	response: ProfileResponse | PhonePreCheckResponse | CreateProfileResponse,
): { kycToken: string; kycApplicantId: string } | null {
	// Check if step-up is required using multiple indicators:
	// 1. isStepUpRequired: true (new profile registration endpoint)

	if (!response.isStepUpRequired) {
		return null;
	}

	// Extract KYC data from response
	const userActivityId = response.userActivityId;

	// Check both locations for the KYC token:
	// 1. New format: compliancePayload directly on response (profile/registration endpoint)
	// 2. Legacy format: actionMetadata.providerSDKPayload
	const kycToken =
		"compliancePayload" in response && response.compliancePayload;

	// Both fields are required for KYC verification
	if (!userActivityId || !kycToken) {
		console.warn("⚠️ Step-up required but missing KYC data:", {
			userActivityId,
			kycToken,
		});
		return null;
	}

	return {
		kycToken,
		kycApplicantId: userActivityId,
	};
}

// Store actions
export const rampsActions = {
	setToken: (token: string) => {
		rampsStore.setState((prev) => ({ ...prev, token }));
	},

	setWalletAddress: (walletAddress: string) => {
		rampsStore.setState((prev) => ({ ...prev, walletAddress }));
	},

	setApiVersion: (apiVersion: "v2" | "v3" | "v5") => {
		rampsStore.setState((prev) => ({ ...prev, apiVersion }));
	},

	/**
	 * Seed the locale from URL detection at bootstrap. The `source` marker
	 * tells subsequent setters (setContextData / setPrepareData) whether the
	 * lang is partner-supplied and therefore must not be overwritten.
	 */
	setLocaleFromUrl: (locale: {
		tag: string;
		source: "url-app-languages" | "url-lang" | "browser" | "default";
	}) => {
		rampsStore.setState((prev) => ({
			...prev,
			lang: locale.tag,
			langSource: locale.source,
		}));
	},

	// Set transaction context data from URL params (v2/v5 flow)
	// These are the same fields as prepare endpoint, but sourced from URL
	setContextData: (data: {
		operation: string;
		lang: string;
		assetCode: string;
		walletProvider: string;
	}) => {
		rampsStore.setState((prev) => {
			// Honor URL-driven locale — partner intent beats route-context data.
			const isUrlDriven =
				prev.langSource === "url-app-languages" ||
				prev.langSource === "url-lang";
			return {
				...prev,
				operation: normalizeOperation(data.operation),
				lang: isUrlDriven ? prev.lang : data.lang,
				langSource: isUrlDriven ? prev.langSource : "prepare",
				assetCode: data.assetCode,
				walletProvider: data.walletProvider,
				isPrepareComplete: true,
			};
		});
	},

	// Set prepare response data (v3 flow)
	// Includes transactionId which may differ from URL param
	setPrepareData: (data: {
		operation: string;
		lang: string;
		assetCode: string;
		transactionId: string;
		walletProvider: string;
		amount?: string; // Add optional amount
	}) => {
		rampsStore.setState((prev) => {
			// Honor URL-driven locale — see setContextData for rationale.
			const isUrlDriven =
				prev.langSource === "url-app-languages" ||
				prev.langSource === "url-lang";
			return {
				...prev,
				operation: normalizeOperation(data.operation),
				lang: isUrlDriven ? prev.lang : data.lang,
				langSource: isUrlDriven ? prev.langSource : "prepare",
				assetCode: data.assetCode,
				transactionId: data.transactionId,
				walletProvider: data.walletProvider,
				prepareAmount: data.amount, // Store prepare amount
				isPrepareComplete: true,
			};
		});
	},

	setPrepareFailed: () => {
		rampsStore.setState((prev) => ({ ...prev, isPrepareFailed: true }));
	},

	setCountries: (countries: Country[]) => {
		rampsStore.setState((prev) => ({ ...prev, countries }));
	},

	setSuggestedCountry: (suggestedCountry: string) => {
		rampsStore.setState((prev) => ({ ...prev, suggestedCountry }));
	},

	setCalculatorData: (data: {
		country: string;
		state: string;
		amount: string;
		currency: string;
	}) => {
		rampsStore.setState((prev) => {
			return enforcePassportIdRule({
				...prev,
				payoutCountry: data.country,
				state: data.state,
				amount: data.amount,
				currency: data.currency,
				...calculateTotals(data.amount),
			});
		});
	},

	setPayoutCountry: (country: string) => {
		rampsStore.setState((prev) =>
			enforcePassportIdRule({
				...prev,
				payoutCountry: country,
			}),
		);
	},

	setSubdivision: (subdivision: string, country: string) => {
		rampsStore.setState((prev) =>
			enforcePassportIdRule({
				...prev,
				state: subdivision,
				payoutCountry: country,
			}),
		);
	},

	setLocks: (locks: string[]) => {
		rampsStore.setState((prev) => ({
			...prev,
			locks: new Set(locks),
		}));
	},

	isFieldLocked: (fieldName: string): boolean => {
		const state = rampsStore.state;
		return state.locks.has(fieldName);
	},

	setPhoneData: (data: {
		phone: string;
		phoneCountryCode: string;
		phoneCountrySelection?: string;
	}) => {
		rampsStore.setState((prev) => ({ ...prev, ...data }));
	},

	setPhoneCountrySelection: (phoneCountrySelection: string) => {
		rampsStore.setState((prev) => ({ ...prev, phoneCountrySelection }));
	},

	setPersonalInfo: (data: {
		firstName: string;
		middleName?: string;
		lastName: string;
		secondLastName?: string;
	}) => {
		rampsStore.setState((prev) => ({
			...prev,
			firstName: data.firstName,
			middleName: data.middleName || "",
			lastName: data.lastName,
			secondLastName: data.secondLastName || "",
		}));
	},

	confirmLegalName: (confirmed: boolean) => {
		rampsStore.setState((prev) => ({
			...prev,
			isLegalNameConfirmed: confirmed,
		}));
	},

	setAddressData: (data: {
		address: string;
		address2?: string;
		city: string;
		zipCode: string;
		addressCountry: string;
		addressState: string;
		isManualAddress: boolean;
	}) => {
		rampsStore.setState((prev) => ({
			...prev,
			...data,
			address2: data.address2 || "", // Ensure address2 is always a string
		}));
	},

	setIdentificationData: (data: {
		idType: string;
		idNumber: string;
		idIssueCountry: string;
		idIssueState: string;
	}) => {
		rampsStore.setState((prev) => ({ ...prev, ...data }));
	},

	setCurrentStep: (step: RampsState["currentStep"]) => {
		rampsStore.setState((prev) => ({ ...prev, currentStep: step }));
	},

	setTransactionId: (transactionId: string) => {
		rampsStore.setState((prev) => ({ ...prev, transactionId }));
	},

	setQuoteResponse: (quoteResponse: QuoteResponse) => {
		// Extract transactionId from the first transaction if available
		// This ensures the store always has the latest transactionId for validation
		const newTransactionId = quoteResponse.transactions?.[0]?.transactionId;

		rampsStore.setState((prev) => {
			// If we're getting a new quote with a different transactionId,
			// clear the old validation response and payload since they're tied to the old transaction
			const shouldClearValidation =
				newTransactionId &&
				prev.transactionId &&
				newTransactionId !== prev.transactionId;

			return {
				...prev,
				quoteResponse,
				// Update transactionId if present in the quote response
				...(newTransactionId && { transactionId: newTransactionId }),
				// Clear old validation data when we get a new quote (new transactionId)
				...(shouldClearValidation && {
					transactionResponse: undefined,
					validatePayload: undefined,
				}),
			};
		});
	},

	/**
	 * Clear quote response (e.g. when country or location changes)
	 */
	clearQuoteResponse: () => {
		rampsStore.setState((prev) => ({ ...prev, quoteResponse: undefined }));
	},

	/**
	 * Clear validation data (validatePayload and transactionResponse).
	 * Use when FeeQuote or SendValidate fails so the UI does not show stale data.
	 */
	clearValidationData: () => {
		rampsStore.setState((prev) => ({
			...prev,
			validatePayload: undefined,
			transactionResponse: undefined,
		}));
	},

	// Set routing code for bank services from user selection
	setServiceOptionRoutingCode: (routingCode: string) => {
		rampsStore.setState((prev) => ({
			...prev,
			serviceOptionRoutingCode: routingCode,
		}));
	},

	// Set selected bank choice (full object) for bank services
	setSelectedBankChoice: (choice: PayoutEntityChoice) => {
		rampsStore.setState((prev) => ({
			...prev,
			selectedBankChoice: choice,
			serviceOptionRoutingCode: choice.receiveAgentId,
		}));
	},

	setValidatePayload: (payload: SendCommitRequest) => {
		rampsStore.setState((prev) => ({ ...prev, validatePayload: payload }));
	},

	setTransactionResponse: (
		transactionResponse: RampsState["transactionResponse"],
	) => {
		rampsStore.setState((prev) => ({ ...prev, transactionResponse }));
	},

	setCommitResponse: (commitResponse: SendCommitResponse) => {
		rampsStore.setState((prev) => ({
			...prev,
			commitResponse,
			currentStep: "status",
		}));
	},

	setSenderProfile: (senderProfile: SenderProfile) => {
		rampsStore.setState((prev) => {
			// Helper to check if a value exists and is not empty
			const hasValue = (value: string | undefined) =>
				value && value.trim() !== "";

			// Helper to prefer store value over sender profile value
			const preferStore = (
				storeValue: string,
				profileValue: string | undefined,
				defaultValue = "",
			) => {
				return hasValue(storeValue) ? storeValue : profileValue || defaultValue;
			};

			// Resolve addressCountry first to check if subdivisions are required
			const resolvedAddressCountry = preferStore(
				prev.addressCountry,
				senderProfile.address?.countryCode,
			);

			// Determine addressState based on whether country requires subdivisions
			const countriesWithSubdivisions = getCountriesWithSubdivisions();
			const addressState = countriesWithSubdivisions.includes(
				resolvedAddressCountry,
			)
				? preferStore(
						prev.addressState,
						senderProfile.address?.countrySubdivisionCode,
					)
				: "";

			return enforcePassportIdRule({
				...prev,
				// Personal information - prefer store values
				firstName: preferStore(prev.firstName, senderProfile.firstName),
				middleName: preferStore(prev.middleName, senderProfile.middleName),
				lastName: preferStore(prev.lastName, senderProfile.lastName),
				secondLastName: preferStore(
					prev.secondLastName,
					senderProfile.secondLastName,
				),
				dateOfBirth: preferStore(prev.dateOfBirth, senderProfile.dateOfBirth),
				birthCountryCode: preferStore(
					prev.birthCountryCode,
					senderProfile.birthCountryCode,
				),
				notificationLanguagePreference: preferStore(
					prev.notificationLanguagePreference,
					senderProfile.notificationLanguagePreference,
					"en-US",
				),
				politicalExposedPerson:
					prev.politicalExposedPerson ||
					senderProfile.politicalExposedPerson ||
					false,

				// Address information - prefer store values
				address: preferStore(prev.address, senderProfile.address?.line1),
				address2: preferStore(prev.address2, senderProfile.address?.line2),
				city: preferStore(prev.city, senderProfile.address?.city),
				zipCode: preferStore(prev.zipCode, senderProfile.address?.postalCode),
				addressCountry: resolvedAddressCountry,
				addressState,

				// Phone information - prefer store values
				phone: preferStore(prev.phone, senderProfile.mobilePhone?.number),
				phoneCountryCode:
					hasValue(prev.phoneCountryCode) && prev.phoneCountryCode !== "+1"
						? prev.phoneCountryCode
						: senderProfile.mobilePhone?.countryDialCode?.startsWith("+")
							? senderProfile.mobilePhone.countryDialCode
							: `+${senderProfile.mobilePhone?.countryDialCode || "1"}`,
				// Keep existing phoneCountrySelection if set, otherwise will be initialized on phone page
				phoneCountrySelection: prev.phoneCountrySelection,

				// Identification - prefer store values
				idType: preferStore(
					prev.idType === "state-id" ? "" : prev.idType,
					senderProfile.primaryIdentification?.typeCode,
				),
				idNumber: preferStore(
					prev.idNumber,
					senderProfile.primaryIdentification?.id,
				),
				idIssueCountry: preferStore(
					prev.idIssueCountry,
					senderProfile.primaryIdentification?.issueCountryCode,
				),
				idIssueState: preferStore(
					prev.idIssueState,
					senderProfile.primaryIdentification?.issueCountrySubdivisionCode,
				),
			});
		});
	},

	// setAdditionalInfoGFFP: (
	// 	additionalInfoGFFP: Record<string, string | Record<string, unknown>>,
	// ) => {
	// 	rampsStore.setState((prev) => ({ ...prev, additionalInfoGFFP }));
	// },

	setPayoutCurrency: (payoutCurrency: string) => {
		rampsStore.setState((prev) => ({ ...prev, payoutCurrency }));
	},

	setDateOfBirth: (dateOfBirth: string) => {
		rampsStore.setState((prev) => ({ ...prev, dateOfBirth }));
	},

	setSelectedLocation: (location: PickupLocation | null) => {
		rampsStore.setState((prev) => ({ ...prev, selectedLocation: location }));
	},

	setCashInLocation: (location: CashInLocation | null) => {
		rampsStore.setState((prev) => ({ ...prev, cashInLocation: location }));
	},

	setCashInSendCurrency: (currency: string | undefined) => {
		rampsStore.setState((prev) => ({ ...prev, cashInSendCurrency: currency }));
	},

	/**
	 * Check if a cash-in location has been selected
	 */
	hasCashInLocation: (): boolean => {
		return !!rampsStore.state.cashInLocation?.locationId;
	},

	/**
	 * Get the selected cash-in location ID
	 * Returns undefined if no location is selected
	 */
	getCashInLocationId: (): string | undefined => {
		return rampsStore.state.cashInLocation?.locationId;
	},

	/**
	 * Build StagingInfo for deposit quote request
	 * Returns null if no cash-in location is selected
	 */
	getStagingInfo: (): {
		stageTransaction: true;
		sendLocationAgentPartnerId: string;
		formFreeCompletion: boolean;
	} | null => {
		const location = rampsStore.state.cashInLocation;
		if (!location?.locationId) {
			return null;
		}
		return {
			stageTransaction: true,
			sendLocationAgentPartnerId: location.locationId,
			formFreeCompletion: location.formFreeCompletion,
		};
	},

	/**
	 * Clear cash-in location selection
	 */
	clearCashInLocation: () => {
		rampsStore.setState((prev) => ({ ...prev, cashInLocation: null }));
	},

	/**
	 * Set user coordinates from serviceOptions API response
	 * Normalizes falsy values (null, undefined, empty string, 0) to empty string
	 */
	setUserCoordinates: (
		lat: string | null | undefined,
		lng: string | null | undefined,
	) => {
		rampsStore.setState((prev) => ({
			...prev,
			userLatitude: lat || "",
			userLongitude: lng || "",
		}));
	},

	setMinAmount: (minAmount: number) => {
		rampsStore.setState((prev) => ({ ...prev, minAmount }));
	},

	setMaxAmount: (maxAmount: number) => {
		rampsStore.setState((prev) => ({ ...prev, maxAmount }));
	},

	setMaxSendLimit: (maxSendLimit: number) => {
		rampsStore.setState((prev) => ({ ...prev, maxSendLimit }));
	},

	setKycPollingTime: (kycPollingTime: number) => {
		rampsStore.setState((prev) => ({ ...prev, kycPollingTime }));
	},

	setKycFlowEnabled: (kycFlowEnabled: boolean) => {
		rampsStore.setState((prev) => ({ ...prev, kycFlowEnabled }));
	},

	setCashInFlowEnabled: (cashInFlowEnabled: boolean) => {
		rampsStore.setState((prev) => ({ ...prev, cashInFlowEnabled }));
	},

	setServiceOption: (serviceOption: ServiceOptionCode) => {
		rampsStore.setState((prev) => ({ ...prev, serviceOption }));
	},

	setBankFieldsGFFP: (
		schema: Array<{
			field: string;
			dataType: string;
			required: boolean;
			fieldLabel?: string;
			displayOrder?: string;
			fieldMin?: string;
			fieldMax?: string;
			regEx?: string;
			helpTextLong?: string;
			exampleText?: string;
			enumerationItem?: Array<{ value: string; description: string }>;
		}>,
		values: Record<string, unknown>,
	) => {
		rampsStore.setState((prev) => ({
			...prev,
			bankFieldsGFFP: { schema, values },
			isValidBankAccount: true,
		}));
	},

	setDigitalProfileId: (digitalProfileId: string) => {
		rampsStore.setState((prev) => ({ ...prev, digitalProfileId }));
	},

	// Set digital profile ID from any lookup source
	setDigitalProfile: (digitalProfileId: string, source: "wallet" | "phone") => {
		rampsStore.setState((prev) => ({
			...prev,
			digitalProfileId,
			walletLookupCompleted:
				source === "wallet" ? true : prev.walletLookupCompleted,
			phoneLookupCompleted:
				source === "phone" ? true : prev.phoneLookupCompleted,
		}));
	},

	// Mark wallet lookup as completed (even if no profile found)
	setWalletLookupCompleted: () => {
		rampsStore.setState((prev) => ({
			...prev,
			walletLookupCompleted: true,
		}));
	},

	// Mark phone lookup as completed (even if no profile found)
	setPhoneLookupCompleted: (completed: boolean) => {
		rampsStore.setState((prev) => ({
			...prev,
			phoneLookupCompleted: completed,
		}));
	},

	setProfileCreated: (digitalProfileId: string) => {
		rampsStore.setState((prev) => ({
			...prev,
			digitalProfileId,
			profileCreationCompleted: true,
		}));
	},

	// Mark wallet association as completed
	setWalletAssociationCompleted: () => {
		rampsStore.setState((prev) => ({
			...prev,
			walletAssociationCompleted: true,
		}));
	},

	updateBankFieldsValues: (values: Record<string, unknown>) => {
		rampsStore.setState((prev) => ({
			...prev,
			bankFieldsGFFP: prev.bankFieldsGFFP
				? {
						...prev.bankFieldsGFFP,
						values,
					}
				: undefined,
			isValidBankAccount: true,
		}));
	},

	setSelectedBankAccount: (bankAccount: BankAccount) => {
		rampsStore.setState((prev) => ({
			...prev,
			selectedBankAccount: bankAccount,
			isValidBankAccount: true,
		}));
	},

	clearReturningUserData: () => {
		rampsStore.setState((prev) => ({
			...prev,
			digitalProfileId: undefined,
			selectedBankAccount: undefined,
		}));
	},

	clearSelectedBankAccount: () => {
		rampsStore.setState((prev) => ({
			...prev,
			selectedBankAccount: undefined,
			isValidBankAccount: false,
			bankFieldsGFFP: undefined,
			validatePayload: undefined, // Clear old validation payload to prevent merging old data
			transactionResponse: undefined, // Clear transaction response to trigger re-validation
		}));
	},

	setKycData: (
		kycToken: string,
		kycApplicantId: string,
		source: "api" | "manual" = "manual",
	) => {
		rampsStore.setState((prev) => ({
			...prev,
			kycToken,
			kycApplicantId,
			kycDataSource: source,
			isStepUpRequired: source === "api" ? true : prev.isStepUpRequired,
		}));
	},

	setStepUpRequired: (isRequired: boolean) => {
		rampsStore.setState((prev) => ({
			...prev,
			isStepUpRequired: isRequired,
		}));
	},

	setKycVerificationCompleted: (verificationData?: OnfidoCompleteData) => {
		rampsStore.setState((prev) => {
			const documentIds: string[] = [];

			// Extract document IDs in consistent order
			if (verificationData?.document_front?.id) {
				documentIds.push(verificationData.document_front.id);
			}
			if (verificationData?.document_back?.id) {
				documentIds.push(verificationData.document_back.id);
			}
			if (verificationData?.face?.id) {
				documentIds.push(verificationData.face.id);
			}

			return {
				...prev,
				kycVerificationCompleted: true,
				kycVerificationData: verificationData,
				kycDocumentIds: documentIds,
			};
		});
	},

	setKycCheckStatus: (status: RampsState["kycCheckStatus"]) => {
		rampsStore.setState((prev) => ({ ...prev, kycCheckStatus: status }));
	},

	setIsAllowedToProceed: (isAllowedToProceed: boolean) => {
		rampsStore.setState((prev) => ({ ...prev, isAllowedToProceed }));
	},

	/**
	 * Mark a transactionId as having a successful refund commit submitted in
	 * this session. Idempotent. Used to gate the Cancel button on the status
	 * page so users can't submit duplicate cancellations during the backend's
	 * propagation window (see RAMPS HAR analysis — `isRefundAllowedFlag` may
	 * remain `true` for minutes after a successful PUT /refund/commit).
	 */
	markCancellationSubmitted: (txId: string) => {
		if (!txId) return;
		rampsStore.setState((prev) => {
			if (prev.cancellationSubmittedTxIds.includes(txId)) return prev;
			return {
				...prev,
				cancellationSubmittedTxIds: [...prev.cancellationSubmittedTxIds, txId],
			};
		});
	},

	resetForm: () => {
		rampsStore.setState(() => ({
			...initialState,
			// Reset profile tracking on new transaction
			walletLookupCompleted: false,
			phoneLookupCompleted: false,
			profileCreationCompleted: false,
			walletAssociationCompleted: false,
		}));
	},
};
